import _ from 'lodash';

import {
  firebaseAddDoc,
  firebaseDeleteDoc,
  firebaseGetAll,
  firebaseGetBy,
  firebaseGetById,
  firebaseUpdateDoc,
  firebaseGetDocRef,
} from '@client/api/firebase.api';
import {
  cleanData,
  createMetadata,
  getAuthUser,
  updateMetadata,
} from '@client/services/common.service';
import { firebaseAuth } from '@client/services/firebase.config';
import axiosInstance from '@client/utils/axios.util';
import { isNewMode } from '@client/utils/form.util';
import { logger } from '@client/utils/logger.util';
import {
  CTenantsPath,
  CSubscriptionsPath,
  EErrorType,
  ESort,
  EStatus,
  type ETier,
  type ITenant,
  type IApiKey,
} from '@common/types';

import type { TPaginateProps } from '@client/types/antd.type';
import type { TablePaginationConfig } from 'antd/es/table/interface';
import type { AxiosResponse } from 'axios';
import type { User } from 'firebase/auth';
import type { DocumentData, DocumentReference } from 'firebase/firestore';

export const getSubscriptionRefs = (ids: string[]): DocumentReference[] => {
  if (!ids?.length) return [];
  return ids.map((id) =>
    firebaseGetDocRef({
      collectionPath: CSubscriptionsPath,
      document: id,
    }),
  );
};

export const getTenant = async (
  id: Required<ITenant['id']>,
): Promise<ITenant | null> => {
  try {
    if (isNewMode(id)) return null;
    const tenant = (await firebaseGetById({
      collectionPath: CTenantsPath,
      document: id as string,
    })) as ITenant;

    if (tenant && tenant.subscriptionRefs) {
      (tenant as any).subscriptionIds = tenant.subscriptionRefs.map(
        (ref) => ref.id,
      );
    }

    return tenant;
  } catch (error) {
    logger.error('getTenant', error);
    throw error;
  }
};

export const createTenant = async (data: ITenant) => {
  const user = firebaseAuth?.currentUser;
  if (!user) throw new Error(EErrorType.NOT_AUTHENTICATED);

  const subscriptionRefs = getSubscriptionRefs(
    (data as any).subscriptionIds || [],
  );

  return firebaseAddDoc<ITenant>({
    collectionPath: CTenantsPath,
    data: {
      ...cleanData(data),
      subscriptionRefs,
      status: EStatus.PROCESSING,
      metadata: createMetadata(user),
    },
  });
};

export const updateTenant = async (
  id: Required<ITenant['id']>,
  data: ITenant,
) => {
  try {
    const user = getAuthUser();

    const existing = await firebaseGetById({
      collectionPath: CTenantsPath,
      document: id,
    });

    let existingMetadata = {};
    if (existing) {
      existingMetadata = existing.metadata ?? {};
    }

    const subscriptionRefs = getSubscriptionRefs(
      (data as any).subscriptionIds || [],
    );

    return firebaseUpdateDoc<ITenant>({
      collectionPath: CTenantsPath,
      document: id as string,
      data: {
        ...cleanData(data),
        subscriptionRefs,
        metadata: {
          ...existingMetadata,
          ...updateMetadata(user as User),
        },
      },
    });
  } catch (error) {
    logger.error('updateTenant', error);
    throw error;
  }
};

export const deleteTenant = async (id: Required<ITenant['id']>) =>
  firebaseDeleteDoc({
    collectionPath: CTenantsPath,
    document: id as string,
  });

export const deactivateTenant = async (id: Required<ITenant['id']>) => {
  await firebaseUpdateDoc({
    collectionPath: CTenantsPath,
    document: id as string,
    data: {
      status: EStatus.DEACTIVATED,
    },
  });

  return getUserTenants();
};

export const getAllTenants = async (
  params?: TPaginateProps & TablePaginationConfig,
) => {
  logger.debug('params', params);

  try {
    const features = await firebaseGetAll<ITenant & { id: string }>({
      collectionPath: CTenantsPath,
    });

    const data: ITenant[] = [];
    if ('length' in features) {
      features?.forEach((doc: ITenant & { id: string }) => {
        data.push(_.merge(doc, { id: doc.id }) as ITenant);
      });
    }

    return data;
  } catch (error) {
    logger.error('getTenants', error);
    throw error;
  }
};

export const getUserTenants = async (
  params?: TPaginateProps & TablePaginationConfig,
) => {
  const user = getAuthUser();

  try {
    const result =
      (await firebaseGetBy({
        collectionPath: CTenantsPath,
        wheres: [{ field: 'metadata.createdBy.uid', value: user.uid }],
        optional: {
          order: 'metadata.updatedAt',
          orderDirection: ESort.DESC,
          limit: params?.pageSize,
        },
      })) || [];

    return {
      [CTenantsPath]: result?.map(
        (doc: DocumentData) => _.merge({ id: doc.id }, doc) as ITenant,
      ),
      total: result?.length,
    };
  } catch (error) {
    logger.error('getTenants error', error);
    throw error;
  }
};

export const getTenantsBySubscription = async (subscriptionId: string) => {
  try {
    const subRef = firebaseGetDocRef({
      collectionPath: CSubscriptionsPath,
      document: subscriptionId,
    });

    const tenantsByRef = await firebaseGetBy<ITenant>({
      collectionPath: CTenantsPath,
      wheres: [
        {
          field: 'subscriptionRefs',
          operator: 'array-contains',
          value: subRef,
        },
      ],
    });

    const tenantsById = await firebaseGetBy<ITenant>({
      collectionPath: CTenantsPath,
      wheres: [
        {
          field: 'subscriptionIds',
          operator: 'array-contains',
          value: subscriptionId,
        },
      ],
    });

    return _.uniqBy([...tenantsByRef, ...tenantsById], 'id');
  } catch (error) {
    logger.error('getTenantsBySubscription', error);
    throw error;
  }
};

export const generateApiKey = async (
  user: User = firebaseAuth.currentUser,
  tenant: ITenant,
  expiresAt?: Date | string | null,
): Promise<AxiosResponse<{ apiKey: string; apiKeyId: string }>> =>
  axiosInstance
    .post(
      `/tenants/${tenant.id}/api-keys`,
      {
        ...(expiresAt && {
          expiresAt:
            expiresAt instanceof Date ? expiresAt.toISOString() : expiresAt,
        }),
      },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${await user.getIdToken()}`,
        },
      },
    )
    .catch((error) => {
      logger.error('Failed to Generate API Key', error);
      throw error;
    });

export const getApiKeys = async (
  user: User,
  tenantId: string,
): Promise<AxiosResponse<IApiKey[]>> =>
  axiosInstance.get(`/tenants/${tenantId}/api-keys`, {
    headers: {
      Authorization: `Bearer ${await user.getIdToken()}`,
    },
  });

export const activateApiKey = async (
  user: User,
  tenantId: string,
  apiKeyId: string,
): Promise<AxiosResponse<{ message: string }>> =>
  axiosInstance.patch(
    `/tenants/${tenantId}/api-keys/${apiKeyId}/activate`,
    {},
    {
      headers: {
        Authorization: `Bearer ${await user.getIdToken()}`,
      },
    },
  );

export const deactivateApiKey = async (
  user: User,
  tenantId: string,
  apiKeyId: string,
): Promise<AxiosResponse<{ message: string }>> =>
  axiosInstance.patch(
    `/tenants/${tenantId}/api-keys/${apiKeyId}/deactivate`,
    {},
    {
      headers: {
        Authorization: `Bearer ${await user.getIdToken()}`,
      },
    },
  );

export const deleteApiKey = async (
  user: User,
  tenantId: string,
  apiKeyId: string,
): Promise<AxiosResponse<{ message: string }>> =>
  axiosInstance.delete(`/tenants/${tenantId}/api-keys/${apiKeyId}`, {
    headers: {
      Authorization: `Bearer ${await user.getIdToken()}`,
    },
  });

export const getTierLimits = async (tenant: ITenant, tier: ETier) => {
  try {
    const { data } = await axiosInstance.get(
      `/tenants/${tenant?.id ?? 'new'}/tiers/${tier}/limits`,
    );
    return data;
  } catch (error) {
    logger.error(`Failed to Get ${tier} tier limits`, error);
    throw error;
  }
};
